import type { GameModeId, ModeRules } from '@game/types';

/**
 * ModeRegistry
 * Single source of truth for all game mode rule profiles.
 * Every mode is a read-only ModeRules object keyed by its GameModeId.
 */
export const MODE_REGISTRY: Record<GameModeId, ModeRules> = {
  // ── Minecraft-Like ──────────────────────────────────────────────────────

  creative: {
    id: 'creative',
    name: 'Creative',
    worldEditing: 'mineAndPlace',
    inventoryModel: 'creative',
    worldPersistence: 'persistent',
    combat: 'none',
    blockDrops: 'none',
    crafting: 'full',
    damage: 'disabled',
    respawn: 'instant',
  },

  survival: {
    id: 'survival',
    name: 'Survival',
    worldEditing: 'mineAndPlace',
    inventoryModel: 'survivalStacks',
    worldPersistence: 'persistent',
    combat: 'pve',
    blockDrops: 'toolScaledDrop',
    crafting: 'full',
    damage: 'playersAndMobs',
    respawn: 'instant',
  },

  adventure: {
    id: 'adventure',
    name: 'Adventure',
    worldEditing: 'none',
    inventoryModel: 'adventureLoadout',
    worldPersistence: 'session',
    combat: 'pve',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'playersAndMobs',
    respawn: 'limitedLives',
  },

  freePlay: {
    id: 'freePlay',
    name: 'Free Play',
    worldEditing: 'none',
    inventoryModel: 'matchLoadout',
    worldPersistence: 'persistent',
    combat: 'pvp',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'playersAndMobs',
    respawn: 'instant',
  },

  driving: {
    id: 'driving',
    name: 'Driving',
    worldEditing: 'none',
    inventoryModel: 'matchLoadout',
    worldPersistence: 'persistent',
    combat: 'none',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'environmentOnly',
    respawn: 'instant',
  },

  // ── PG3D-Like ───────────────────────────────────────────────────────────

  lobby: {
    id: 'lobby',
    name: 'Lobby',
    worldEditing: 'none',
    inventoryModel: 'matchLoadout',
    worldPersistence: 'lobby',
    combat: 'none',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'disabled',
    respawn: 'instant',
  },

  arenaPvp: {
    id: 'arenaPvp',
    name: 'Arena PvP',
    worldEditing: 'none',
    inventoryModel: 'matchLoadout',
    worldPersistence: 'matchEphemeral',
    combat: 'pvp',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'playersAndMobs',
    respawn: 'instant',
  },

  teamPvp: {
    id: 'teamPvp',
    name: 'Team vs Team',
    worldEditing: 'none',
    inventoryModel: 'matchLoadout',
    worldPersistence: 'matchEphemeral',
    combat: 'teamPvp',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'playersAndMobs',
    respawn: 'spawnWave',
  },

  battleRoyale: {
    id: 'battleRoyale',
    name: 'Battle Royale',
    worldEditing: 'none',
    inventoryModel: 'battleRoyalePickups',
    worldPersistence: 'matchEphemeral',
    combat: 'battleRoyale',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'playersAndMobs',
    respawn: 'elimination',
  },

  buildFight: {
    id: 'buildFight',
    name: 'Build Fight',
    worldEditing: 'limitedBuild',
    inventoryModel: 'matchLoadout',
    worldPersistence: 'matchEphemeral',
    combat: 'pvp',
    blockDrops: 'none',
    crafting: 'disabled',
    damage: 'playersAndMobs',
    respawn: 'instant',
  },
} as const;

/** Retrieve a mode by id. Returns `null` for unknown ids. */
export function getMode(modeId: string): ModeRules | null {
  return (MODE_REGISTRY as Record<string, ModeRules>)[modeId] ?? null;
}
